// Main App Component for TradeOS Dashboard

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AppRouter } from './router';
import { useProfileStore } from './stores/profileStore';
import { useAuthStore, useAuthHydrated } from './stores/authStore';
import { RealtimeProvider, useRealtimeStore } from './stores/realtimeStore';
import { useTradingStore } from './stores/tradingStore';
import { tradeClawKeys } from './api/tradeclaw';
import { identifyUser, resetIdentity } from './lib/analytics';

// Custom retry function that doesn't retry on 4xx errors
const shouldRetryQuery = (failureCount: number, error: unknown): boolean => {
  // Don't retry on 4xx errors (client errors)
  if (error instanceof Error) {
    // Check for HTTP status in error message or properties
    const message = error.message.toLowerCase();
    if (
      message.includes('401') ||
      message.includes('403') ||
      message.includes('404') ||
      message.includes('400') ||
      message.includes('409') ||
      message.includes('422') ||
      message.includes('429')
    ) {
      return false;
    }
    // Check for status property on error
    const errorWithStatus = error as Error & { status?: number };
    if (errorWithStatus.status && errorWithStatus.status >= 400 && errorWithStatus.status < 500) {
      return false;
    }
  }
  // Retry up to 3 times for 5xx errors or network issues
  return failureCount < 3;
};

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 10, // 10 seconds — reduced flicker
      gcTime: 1000 * 60 * 5, // 5 minutes garbage collection
      retry: shouldRetryQuery,
      refetchOnWindowFocus: false, // Disabled to prevent tab-switch flicker
      refetchOnReconnect: true, // Re-fetch after network reconnect
    },
  },
});

/**
 * Bridge component: invalidates React Query caches when WebSocket
 * pushes new data, so polling queries stay fresh without waiting
 * for their next interval tick.
 *
 * FIX: Debounce invalidations to prevent UI flickering.
 * Without debouncing, rapid-fire WS messages (trades, signals, alerts)
 * triggered simultaneous cache invalidations across 6+ query keys,
 * causing the Dashboard to re-render dozens of times per second as each
 * query independently refetched and updated its data slice.
 *
 * Now we collect "dirty" categories and flush a single batched
 * invalidation at most once every 2 seconds.
 */
const INVALIDATION_DEBOUNCE_MS = 5000;

const WebSocketCacheInvalidator: React.FC = () => {
  // Track which categories have been dirtied since the last flush
  const dirtyRef = useRef<Set<string>>(new Set());
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flush = useCallback(() => {
    const dirty = dirtyRef.current;
    if (dirty.size === 0) return;

    if (dirty.has('trades')) {
      queryClient.invalidateQueries({ queryKey: tradeClawKeys.portfolio() });
      queryClient.invalidateQueries({ queryKey: tradeClawKeys.positions() });
      queryClient.invalidateQueries({ queryKey: tradeClawKeys.activityFeed() });
      queryClient.invalidateQueries({ queryKey: tradeClawKeys.status() });
    }
    if (dirty.has('closedPositions')) {
      queryClient.invalidateQueries({ queryKey: ['tradeclaw', 'closedPositions'] });
    }
    if (dirty.has('signals')) {
      queryClient.invalidateQueries({ queryKey: tradeClawKeys.intents() });
      queryClient.invalidateQueries({ queryKey: tradeClawKeys.opportunities() });
    }
    if (dirty.has('risk')) {
      queryClient.invalidateQueries({ queryKey: tradeClawKeys.riskExposure() });
      queryClient.invalidateQueries({ queryKey: tradeClawKeys.riskDrawdown() });
      queryClient.invalidateQueries({ queryKey: tradeClawKeys.riskEvents() });
    }

    dirty.clear();
    timerRef.current = null;
  }, []);

  const markDirty = useCallback(
    (category: string) => {
      dirtyRef.current.add(category);
      // Schedule a flush if one isn't already pending
      if (timerRef.current === null) {
        timerRef.current = setTimeout(flush, INVALIDATION_DEBOUNCE_MS);
      }
    },
    [flush],
  );

  useEffect(() => {
    const unsubs: Array<() => void> = [];

    // Invalidate position/portfolio queries on WS position updates
    unsubs.push(
      useRealtimeStore.subscribe(
        (s) => s.recentTrades.length,
        () => markDirty('trades'),
      ),
    );

    // Invalidate closed positions query when a position:close WS event arrives
    unsubs.push(
      useTradingStore.subscribe(
        (s) => s.closedPositions.length,
        () => markDirty('closedPositions'),
      ),
    );

    // Invalidate signal queries on new WS signals
    unsubs.push(
      useRealtimeStore.subscribe(
        (s) => s.signals.length,
        () => markDirty('signals'),
      ),
    );

    // Invalidate risk queries on risk alerts
    unsubs.push(
      useRealtimeStore.subscribe(
        (s) => s.riskAlerts.length,
        () => markDirty('risk'),
      ),
    );

    return () => {
      unsubs.forEach((u) => u());
      // Clear any pending flush on unmount
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [markDirty]);

  return null;
};

// App initialization wrapper - waits for auth hydration and profile loading before rendering
// This prevents flash of unauthenticated state and ensures proper initialization order
const AppInitializer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { fetchProfiles, isLoaded, isLoading } = useProfileStore();
  const { isAuthenticated, user } = useAuthStore();
  const hasHydrated = useAuthHydrated();
  const [profileFetchStarted, setProfileFetchStarted] = useState(false);

  // Sync PostHog identity with auth state.
  // Runs after hydration so we don't fire on the initial undefined state.
  useEffect(() => {
    if (!hasHydrated) return;
    if (isAuthenticated && user) {
      identifyUser(user.accountId, {
        email: user.email,
        role: user.role,
        createdAt: user.createdAt,
        interests: user.interests as Record<string, unknown> | undefined,
      });
    } else if (hasHydrated && !isAuthenticated) {
      resetIdentity();
    }
  }, [hasHydrated, isAuthenticated, user]);

  // After hydration: verify stored token is still valid against the backend.
  // Handles the case where the DB was wiped (or token expired server-side) while
  // a stale JWT remains in localStorage — Safari / persistent browsers would
  // otherwise rehydrate as isAuthenticated=true, attempt profile/API calls, and
  // crash into the React error boundary with "unexpected error".
  const { logout } = useAuthStore();
  useEffect(() => {
    if (!hasHydrated || !isAuthenticated) return;

    const token = useAuthStore.getState().token;
    if (!token) {
      // Corrupted state: marked authenticated but no token present
      logout();
      return;
    }

    // Lightweight server-side token verification
    fetch('/api/alpha/me', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (res) => {
        // Any 4xx or 5xx auth response means the token is no longer valid in the DB
        if (res.status === 401 || res.status === 403 || res.status === 500) {
          logout();
          return;
        }
        if (res.ok) {
          const account = await res.json().catch(() => null) as {
            account_id?: string;
            email?: string;
            role?: string;
            created_at?: string;
            last_login_at?: string | null;
            interests?: Record<string, unknown>;
            email_verified?: boolean;
            email_verified_at?: string | null;
            verification_required_for_trusted_feedback?: boolean;
          } | null;
          const currentUser = useAuthStore.getState().user;
          if (account?.account_id && currentUser) {
            useAuthStore.getState().setUser({
              ...currentUser,
              accountId: account.account_id,
              email: account.email ?? currentUser.email,
              role: account.role ?? currentUser.role,
              createdAt: account.created_at ?? currentUser.createdAt,
              lastLoginAt: account.last_login_at ?? currentUser.lastLoginAt,
              interests: account.interests ?? currentUser.interests,
              emailVerified: account.email_verified ?? false,
              emailVerifiedAt: account.email_verified_at ?? undefined,
              verificationRequiredForTrustedFeedback: account.verification_required_for_trusted_feedback ?? true,
            });
          }
        }
      })
      .catch(() => {
        // Network / server unreachable — do NOT logout; backend may be starting up
      });
  }, [hasHydrated, isAuthenticated, logout]);

  // Fetch profiles only after auth has hydrated AND user is authenticated
  // This prevents unnecessary API calls and ensures proper initialization order
  useEffect(() => {
    // Wait for auth to hydrate from localStorage first
    if (!hasHydrated) {
      return;
    }

    // Only fetch profiles if user is authenticated and we haven't loaded yet
    // Also guard against double-fetching with profileFetchStarted
    if (isAuthenticated && !isLoaded && !isLoading && !profileFetchStarted) {
      setProfileFetchStarted(true);
      fetchProfiles().catch(console.error);
    }
  }, [hasHydrated, isAuthenticated, isLoaded, isLoading, profileFetchStarted, fetchProfiles]);

  // Show loading state while auth is hydrating OR profile is loading
  // This prevents the flicker between "Initializing..." and "Checking profile..."
  // by waiting until everything is ready before rendering the app
  if (!hasHydrated) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-400">Initializing...</p>
        </div>
      </div>
    );
  }

  // If authenticated, wait for profile to finish loading before rendering
  // This prevents the ProtectedRoute from showing its own loader
  if (isAuthenticated && (isLoading || !isLoaded)) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-400">Loading profile...</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

// Auth-aware wrapper: only connects WebSocket when user is authenticated.
// Prevents 403 storm on landing page (unauthenticated) which crashes Safari's
// stricter error boundary via the exponential-backoff reconnect loop.
// Chrome silently swallows WS errors; Safari does not.
const AuthAwareRealtimeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuthStore();
  const hasHydrated = useAuthHydrated();
  // Only autoConnect once hydration is complete AND user is authenticated.
  // On landing page: isAuthenticated=false → autoConnect=false → no WS attempt → no 403.
  return (
    <RealtimeProvider autoConnect={isAuthenticated && hasHydrated}>
      {children}
    </RealtimeProvider>
  );
};

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthAwareRealtimeProvider>
        <WebSocketCacheInvalidator />
        <AppInitializer>
          <AppRouter />
        </AppInitializer>
      </AuthAwareRealtimeProvider>
    </QueryClientProvider>
  );
};

export default App;
